import { useCallback, useEffect, useRef, useState } from 'react';

const isCanceled = (error) =>
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'AbortError';

/**
 * The fetch-per-page boilerplate this codebase repeats by hand, in one place.
 *
 * Additive: no existing page is changed. It guards on both cancellation idioms
 * already in use here, because api.js re-rejects cancellations untouched and
 * different screens check different properties.
 *
 * `deps` must keep a stable length at a given call site (React forbids a
 * changing dependency-array length).
 */
export default function useApiResource(
  request,
  {
    deps = [],
    enabled = true,
    debounceMs = 0,
    keepPreviousData = true,
    errorMessage = 'We could not load this data. Try refreshing the page.',
  } = {},
) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(enabled);
  const [reloadKey, setReloadKey] = useState(0);

  // Held in a ref so an inline arrow function does not retrigger the effect.
  const requestRef = useRef(request);
  requestRef.current = request;

  const refetch = useCallback(() => setReloadKey((value) => value + 1), []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    let timer;

    const run = () => {
      setLoading(true);
      setError('');
      if (!keepPreviousData) setData(null);
      requestRef
        .current({ signal: controller.signal })
        .then((result) => {
          if (!controller.signal.aborted) setData(result);
        })
        .catch((requestError) => {
          if (controller.signal.aborted || isCanceled(requestError)) return;
          setError(requestError?.response?.data?.detail || errorMessage);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    };

    if (debounceMs) timer = setTimeout(run, debounceMs);
    else run();

    return () => {
      if (timer) clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, debounceMs, reloadKey, ...deps]);

  return { data, error, loading, refetch };
}
